import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { PasswordInput } from './PasswordInput';
import { OneTrustIcon } from './OneTrustIcon';
import { OneTrustSignupModal } from './OneTrustSignupModal';
import { TermsConditionsModal } from './TermsConditionsModal';

export function Auth() {
  const [view, setView] = useState<'sign_in' | 'sign_up'>('sign_in');
  const [signUpError, setSignUpError] = useState<string>('');
  const [signInError, setSignInError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user?.email) {
        // Ensure profile exists for the user
        if (event === 'SIGNED_UP' || event === 'SIGNED_IN') {
          try {
            // Check if profile exists
            const { data: profile, error } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();
            
            // If no profile exists, create one
            if (error && error.code === 'PGRST116') {
              await supabase
                .from('profiles')
                .insert({
                  id: session.user.id,
                  email: session.user.email || '',
                  first_name: '',
                  last_name: ''
                });
            }
          } catch (error) {
            console.error('Error checking/creating profile:', error);
          }
        }
        
        navigate('/');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSignUpError('');
    setSignInError('');

    try {
      if (view === 'sign_up') {
        // Check if email already exists
        const { data: existingProfiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', email)
          .limit(1);

        if (existingProfiles && existingProfiles.length > 0) {
          setSignUpError('This email is already registered. Please use a different email or sign in.');
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (error: any) {
      if (view === 'sign_up') {
        setSignUpError(error.message);
      } else {
        setSignInError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 transition-all duration-300">
          <div className="relative mb-8">
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] text-center">
              {view === 'sign_in' ? 'Sign In' : 'Create Account'}
            </h2>
            {view === 'sign_up' && (
              <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-50">
                <button
                  onClick={() => setIsSignupModalOpen(true)}
                  className="relative group"
                  aria-label="Learn more about OneTrust integration"
                >
                  <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
                  <OneTrustIcon size="lg" />
                </button>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#499da8] focus:ring-[#499da8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
              />
            </div>

            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              label="Password"
              error={view === 'sign_in' ? signInError : signUpError}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#499da8] transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Loading...' : view === 'sign_in' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setView(view === 'sign_in' ? 'sign_up' : 'sign_in');
                setSignUpError('');
                setSignInError('');
              }}
              className="text-[#499da8] hover:text-[#3d8289] transition-colors duration-300"
            >
              {view === 'sign_in' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Terms and Conditions Link - Only show on sign up */}
          {view === 'sign_up' && (
            <div className="mt-4 text-center">
              <div className="flex items-center justify-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  By signing up, you agree to our{' '}
                  <Link 
                    to="/terms" 
                    className="text-[#499da8] hover:text-[#3d8289] transition-colors duration-300 underline"
                  >
                    Terms and Conditions
                  </Link>
                </p>
                <button
                  onClick={() => setIsTermsModalOpen(true)}
                  className="relative group ml-4"
                  aria-label="Learn more about Terms and Conditions"
                >
                  <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
                  <OneTrustIcon size="lg" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <OneTrustSignupModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
      />

      <TermsConditionsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </div>
  );
}